import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';

// Animations
const float = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(2deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const pulse = keyframes`
  0% { opacity: 0.3; }
  50% { opacity: 0.7; }
  100% { opacity: 0.3; }
`;

const rotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Floating DNA helix elements
const FloatingContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: -1;
  overflow: hidden;
`;

const DNAHelix = styled(motion.div)`
  position: absolute;
  width: ${props => props.size || '100px'};
  height: ${props => props.size || '100px'};
  background: linear-gradient(
    ${props => props.angle || '45deg'},
    rgba(var(--primary-rgb), ${props => props.opacity || 0.1}) 0%,
    rgba(37, 65, 178, ${props => props.opacity || 0.1}) 100%
  );
  border-radius: 50%;
  filter: blur(${props => props.blur || '4px'});
  left: ${props => props.left || '10%'};
  top: ${props => props.top || '10%'};
  animation: ${float} ${props => props.duration || '10s'} infinite ease-in-out,
             ${pulse} ${props => props.pulseDuration || '8s'} infinite ease-in-out;
  animation-delay: ${props => props.delay || '0s'};
`;

const DNAStrand = styled(motion.div)`
  position: absolute;
  width: ${props => props.width || '20px'};
  height: ${props => props.height || '80px'};
  background: linear-gradient(
    ${props => props.angle || '90deg'},
    rgba(var(--primary-rgb), ${props => props.opacity || 0.15}) 0%,
    rgba(37, 65, 178, ${props => props.opacity || 0.15}) 100%
  );
  border-radius: 20px;
  filter: blur(${props => props.blur || '2px'});
  left: ${props => props.left || '20%'};
  top: ${props => props.top || '20%'};
  transform-origin: center;
  animation: ${float} ${props => props.duration || '15s'} infinite ease-in-out;
  animation-delay: ${props => props.delay || '0s'};
  opacity: ${props => props.opacity || 0.2};
`;

const DNANode = styled(motion.div)`
  position: absolute;
  width: ${props => props.size || '8px'};
  height: ${props => props.size || '8px'};
  background: radial-gradient(
    circle,
    rgba(var(--primary-rgb), ${props => props.opacity || 0.6}) 0%,
    rgba(37, 65, 178, ${props => props.opacity || 0.3}) 100%
  );
  border-radius: 50%;
  filter: blur(1px);
  left: ${props => props.left || '30%'};
  top: ${props => props.top || '30%'};
  animation: ${pulse} ${props => props.duration || '6s'} infinite ease-in-out;
  animation-delay: ${props => props.delay || '0s'};
`;

const RotatingElement = styled(motion.div)`
  position: absolute;
  width: ${props => props.size || '150px'};
  height: ${props => props.size || '150px'};
  border: 2px dashed rgba(${props => props.color || '45, 212, 191'}, ${props => props.opacity || 0.2});
  border-radius: 50%;
  left: ${props => props.left || '50%'};
  top: ${props => props.top || '50%'};
  animation: ${rotate} ${props => props.duration || '40s'} infinite linear;
  animation-delay: ${props => props.delay || '0s'};
`;

const ResultsBackgroundElements = () => {
  return (
    <FloatingContainer>
      {/* DNA Helixes */}
      <DNAHelix 
        size="250px" 
        left="5%" 
        top="15%" 
        opacity={0.07} 
        blur="15px" 
        duration="20s"
        angle="30deg"
      />
      <DNAHelix 
        size="180px" 
        left="80%" 
        top="70%" 
        opacity={0.05} 
        blur="12px" 
        duration="25s"
        delay="2s"
        angle="120deg"
      />
      <DNAHelix 
        size="120px" 
        left="75%" 
        top="20%" 
        opacity={0.08} 
        blur="8px" 
        duration="18s"
        delay="5s"
        angle="210deg"
      />
      
      {/* DNA Strands */}
      <DNAStrand 
        width="15px" 
        height="120px" 
        left="25%" 
        top="10%" 
        opacity={0.12} 
        blur="3px" 
        duration="22s"
        delay="1s"
        angle="45deg"
      />
      <DNAStrand 
        width="10px" 
        height="90px" 
        left="60%" 
        top="65%" 
        opacity={0.15} 
        blur="2px" 
        duration="18s"
        delay="3s"
        angle="135deg"
      />
      <DNAStrand 
        width="12px" 
        height="100px" 
        left="15%" 
        top="60%" 
        opacity={0.1} 
        blur="2.5px" 
        duration="20s"
        delay="4s"
        angle="70deg"
      />
      
      {/* DNA Nodes */}
      <DNANode 
        size="12px" 
        left="40%" 
        top="25%" 
        opacity={0.5} 
        duration="8s"
      />
      <DNANode 
        size="15px" 
        left="70%" 
        top="40%" 
        opacity={0.4} 
        duration="10s"
        delay="2s"
      />
      <DNANode 
        size="10px" 
        left="30%" 
        top="55%" 
        opacity={0.6} 
        duration="7s"
        delay="1s"
      />
      <DNANode 
        size="8px" 
        left="85%" 
        top="30%" 
        opacity={0.5} 
        duration="9s"
        delay="3s"
      />
      <DNANode 
        size="6px" 
        left="20%" 
        top="80%" 
        opacity={0.7} 
        duration="6s"
        delay="1.5s"
      />
      
      {/* Rotating Elements */}
      <RotatingElement 
        size="300px" 
        left="20%" 
        top="30%" 
        opacity={0.15} 
        duration="60s"
        color="45, 212, 191"
      />
      <RotatingElement 
        size="400px" 
        left="75%" 
        top="60%" 
        opacity={0.1} 
        duration="80s"
        delay="10s"
        color="37, 65, 178"
      />
      <RotatingElement 
        size="200px" 
        left="50%" 
        top="20%" 
        opacity={0.12} 
        duration="40s"
        delay="5s"
        color="45, 212, 191"
      />
    </FloatingContainer>
  );
};

export default ResultsBackgroundElements; 